import { PlusIcon, TrashIcon } from '@radix-ui/react-icons';
import { IEventChecklist } from '@clickersphere/core/entities/IEventChecklist';
import { Button } from '../../Button';
import { Input } from '../../Input';
import ScrollArea from '../../ScrollArea';
import { useEventChecklistModalController } from './useEventChecklistModalController';

interface IEventChecklistModalProps {
  eventChecklist?: IEventChecklist;
}

export default function EventChecklistModal({
  eventChecklist,
}: IEventChecklistModalProps) {
  const {
    t,
    register,
    errors,
    fields,
    handleAddItem,
    handleRemoveItem,
    handleSubmit,
    handleClose,
    isPending,
  } = useEventChecklistModalController(eventChecklist);

  return (
    <form className="my-4 flex w-full flex-col gap-4" onSubmit={handleSubmit}>
      <Input
        type="text"
        placeholder={t('settings.checklists.modal.fields.name')}
        {...register('name')}
        error={errors.name?.message}
        disabled={isPending}
      />

      <div className="flex items-center justify-between">
        <span className="text-text-secondary text-sm font-medium">
          {t('settings.checklists.modal.items.title')}
        </span>
        <Button
          color="outline"
          className="h-9 w-auto px-4"
          onClick={handleAddItem}
          disabled={isPending}
        >
          <span className="flex items-center gap-1">
            <PlusIcon className="h-4 w-4" />
            {t('settings.checklists.modal.items.add')}
          </span>
        </Button>
      </div>

      {fields.length === 0 && (
        <p className="text-text-tertiary text-sm">
          {t('settings.checklists.modal.items.empty')}
        </p>
      )}

      {typeof errors.items?.message === 'string' && (
        <p className="text-xs text-red-500 dark:text-red-400">
          {errors.items.message}
        </p>
      )}

      {fields.length > 0 && (
        <ScrollArea className="max-h-72">
          <ul className="flex flex-col gap-3 pr-1">
            {fields.map((field, index) => (
              <li key={field.id} className="flex items-start gap-2">
                <div className="flex-1">
                  <Input
                    type="text"
                    placeholder={t('settings.checklists.modal.items.itemTitle')}
                    {...register(`items.${index}.title`)}
                    error={errors.items?.[index]?.title?.message}
                    disabled={isPending}
                  />
                </div>
                <div className="w-24">
                  <Input
                    type="number"
                    placeholder={t(
                      'settings.checklists.modal.items.offsetDays',
                    )}
                    {...register(`items.${index}.offsetDays`, {
                      valueAsNumber: true,
                    })}
                    error={errors.items?.[index]?.offsetDays?.message}
                    disabled={isPending}
                  />
                </div>
                <Button
                  color="danger"
                  className="h-10 w-10 shrink-0 px-0!"
                  aria-label={t('settings.checklists.modal.items.remove')}
                  onClick={() => handleRemoveItem(index)}
                  disabled={isPending}
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        </ScrollArea>
      )}

      <p className="text-text-tertiary text-xs">
        {t('settings.checklists.modal.items.offsetHint')}
      </p>

      <section className="mt-2 flex w-full gap-4">
        <Button
          className="w-40"
          color="ghost"
          labelPath="settings.checklists.modal.cancel"
          disabled={isPending}
          onClick={handleClose}
        />
        <Button
          type="submit"
          labelPath="settings.checklists.modal.submit"
          isLoading={isPending}
          disabled={isPending}
        />
      </section>
    </form>
  );
}
